import { getElementMaxHeight } from '@utils/elements';

/**
 * Získá cílový prvek, na kterém budeme provádět kolabsování.
 * @param collapserSelector Element, na kterém se provádí kolabsování.
 * @returns Cílový prvek pro kolabsování.
 */
const getTargetSelector = (collapserSelector: HTMLDivElement): HTMLElement | null => {
  const targetId = collapserSelector.dataset.collapserTarget;
  if (!collapserSelector) return null;
  const parent =
    collapserSelector.closest(`#${collapserSelector.dataset.collapserParent}`) ||
    collapserSelector.parentElement;

  return parent
    ? parent.querySelector(`[data-collapser-target]`)
    : document.querySelector(`#${targetId}`);
};

/**
 * Funkce pro přepnutí atributu 'aria-expanded' mezi hodnotami true a false.
 *
 * @param targetSelector Cílový HTML element, kterému se má změnit hodnota atributu 'aria-expanded'.
 * @returns Aktuální hodnota atributu 'aria-expanded' po provedené změně.
 */
const toggleAriaExpanded = (targetSelector: HTMLElement): boolean => {
  const isExpanded = targetSelector.getAttribute('aria-expanded') === 'true';
  targetSelector.setAttribute('aria-expanded', `${!isExpanded}`);
  return !isExpanded;
};

export const Collapser = (collapserSelector: HTMLDivElement) => {
  const targetSelector = getTargetSelector(collapserSelector);
  if (!targetSelector) return;

  const togglerTextSelector = collapserSelector.querySelector('[data-collapser-text]');
  const type = collapserSelector.dataset.collapserType || 'expand';
  const { collapserMoreText = 'Zobrazit více', collapserLessText = 'Zobrazit méně' } =
    collapserSelector.dataset;
  const togglerArrowSelector = collapserSelector.querySelector('[data-collapser-arrow]');

  /**
   * Rozbalí obsah tím, že nastaví atribut 'aria-expanded' na hodnotu 'true'
   * a skryje přepínač. Pokud je k dispozici textový obsah přepínače, nastaví
   * ho na zadaný text pro více obsahu.
   */
  const expandContent = () => {
    targetSelector.setAttribute('aria-expanded', 'true');
    collapserSelector.style.display = 'none';
    if (togglerTextSelector) togglerTextSelector.textContent = collapserMoreText;
  };

  /**
   * Přepíná stav obsahu mezi rozbaleným a zabalenyým. Pokud je obsah rozbalený,
   * změní textový obsah přepínače na zadaný text pro méně obsahu, jinak na text
   * pro více obsahu. Pokud je k dispozici šipka přepínače, nastaví její atribut
   * 'aria-expanded' na odpovídající hodnotu.
   */
  const toggleContent = () => {
    const isExpanded = toggleAriaExpanded(targetSelector);
    collapserSelector.setAttribute('aria-expanded', isExpanded ? 'true' : 'false');
    if (togglerTextSelector)
      togglerTextSelector.textContent = isExpanded ? collapserLessText : collapserMoreText;
    if (togglerArrowSelector)
      togglerArrowSelector.setAttribute('aria-expanded', isExpanded ? 'true' : 'false');
  };

  /**
   * Zjišťuje, zda obsah překračuje maximální výšku.
   * @returns True, pokud obsah překračuje maximální výšku, jinak false.
   */
  const isContentOverflowing = () => {
    const maxHeightValue = getElementMaxHeight(targetSelector);
    if (maxHeightValue === 'none') return false;
    return targetSelector.scrollHeight > parseInt(maxHeightValue, 10);
  };

  /**
   * Přidává nebo odebírá třídu pro indikaci překročení výšky obsahu.
   */
  const handleOverflow = () => {
    if (isContentOverflowing()) {
      targetSelector.classList.remove('hide-target');
      collapserSelector.classList.remove('hide-target');
    } else {
      targetSelector.classList.add('hide-target');
      collapserSelector.classList.add('hide-target');
    }
  };

  /**
   * Zachycuje kliknutí na prvek a provádí akci podle typu kolabsování.
   * @param e Událost kliknutí.
   */
  collapserSelector.addEventListener('click', (e: Event) => {
    e.preventDefault();

    if (type === 'toggle') {
      toggleContent();
    } else {
      expandContent();
    }

    handleOverflow();
  });

  handleOverflow();

  const resizeObserver = new ResizeObserver(() => {
    handleOverflow();
  });
  resizeObserver.observe(targetSelector);
};

export default Collapser;
